// Gemeinsame Hilfen für Laufwerk-Seite, Startseite, Vorschau und Betreiber-Konsole.

/** Status einer Datei im Index → Anzeigename. Reihenfolge = Reihenfolge in der Zahlenliste. */
export const STATUS_REIHENFOLGE = ['indexiert', 'in_arbeit', 'wartet', 'hochladen', 'fehler', 'nicht_unterstuetzt', 'zu_gross'] as const;

export const STATUS_LABEL: Record<string, string> = {
  hochladen: 'Wartet auf Inhalt',
  wartet: 'Wartet auf Verarbeitung',
  in_arbeit: 'In Arbeit',
  indexiert: 'Indexiert',
  nicht_unterstuetzt: 'Nicht unterstützt',
  zu_gross: 'Zu gross',
  fehler: 'Fehler',
};

/** Farbe der kleinen Marke je Status (Tailwind-Klassen aus index.css). */
export const STATUS_BADGE: Record<string, string> = {
  indexiert: 'badge-ok',
  in_arbeit: 'badge-warn',
  wartet: 'badge-warn',
  hochladen: 'badge-warn',
  fehler: 'badge-err',
  nicht_unterstuetzt: 'badge-neutral',
  zu_gross: 'badge-neutral',
};

export const statusLabel = (s: string) => STATUS_LABEL[s] ?? s;

/** Status in Anzeigereihenfolge; unbekannte Status kommen hinten dran. */
export function statusZeilen(zahlen: Record<string, number>): { status: string; n: number }[] {
  const bekannt = STATUS_REIHENFOLGE.filter((s) => (zahlen[s] ?? 0) > 0).map((s) => ({ status: s as string, n: zahlen[s] }));
  const rest = Object.keys(zahlen).filter((s) => !(STATUS_REIHENFOLGE as readonly string[]).includes(s) && zahlen[s] > 0)
    .sort().map((s) => ({ status: s, n: zahlen[s] }));
  return [...bekannt, ...rest];
}

/**
 * Fortschritt des Index: indexiert / (gesamt ohne „nicht unterstützt" und „zu gross") — die beiden Gruppen
 * werden nie indexiert und würden den Balken sonst dauerhaft unter 100 % halten.
 */
export function indexFortschritt(zahlen: Record<string, number>, gesamt: number) {
  const fertig = zahlen.indexiert ?? 0;
  const nenner = Math.max(0, gesamt - (zahlen.nicht_unterstuetzt ?? 0) - (zahlen.zu_gross ?? 0));
  const prozent = nenner === 0 ? 0 : fertig >= nenner ? 100 : Math.floor((fertig / nenner) * 100);
  const wartend = (zahlen.hochladen ?? 0) + (zahlen.wartet ?? 0) + (zahlen.in_arbeit ?? 0);
  return { fertig, nenner, prozent, wartend };
}

/** Zahl mit Tausendertrenner nach de-CH (12’345). */
export const zahl = (n: number | null | undefined) => (n ?? 0).toLocaleString('de-CH');

/** Minuten seit dem Zeitpunkt; null, wenn es keinen gibt oder er nicht lesbar ist. */
export function minutenSeit(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, (Date.now() - t) / 60000);
}

/** „vor 3 Min." / „vor 2 Std." / „vor 4 Tagen" — null → „noch nie". */
export function vorZeit(iso: string | null | undefined): string {
  const m = minutenSeit(iso);
  if (m === null) return 'noch nie';
  if (m < 1) return 'vor wenigen Sekunden';
  if (m < 60) return `vor ${Math.floor(m)} Min.`;
  const h = m / 60;
  if (h < 24) return `vor ${Math.floor(h)} Std.`;
  const t = Math.floor(h / 24);
  return t === 1 ? 'vor 1 Tag' : `vor ${t} Tagen`;
}

/** Connector gilt als verstummt: nie gemeldet oder seit mehr als 10 Minuten nichts mehr gehört. */
export const verstummt = (iso: string | null | undefined) => {
  const m = minutenSeit(iso);
  return m === null || m > 10;
};

export function datumCH(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Scan-Dauer lesbar: 0.8 → „0,8 s", 75 → „1 Min. 15 s". */
export function scanDauer(s: number | null | undefined): string {
  if (s === null || s === undefined || Number.isNaN(s)) return '—';
  if (s < 60) return `${s.toLocaleString('de-CH', { maximumFractionDigits: 1 })} s`;
  const m = Math.floor(s / 60);
  return `${m} Min. ${Math.round(s - m * 60)} s`;
}

/** Dateigrösse lesbar: 512 → „512 B", 15360 → „15 KB", 3.4e6 → „3,4 MB". */
export function bytes(n: number | null | undefined): string {
  const b = n ?? 0;
  if (b < 1024) return `${b} B`;
  const einheiten = ['KB', 'MB', 'GB', 'TB'];
  let w = b / 1024; let i = 0;
  while (w >= 1024 && i < einheiten.length - 1) { w /= 1024; i++; }
  return `${w.toLocaleString('de-CH', { maximumFractionDigits: w < 10 ? 1 : 0 })} ${einheiten[i]}`;
}
